/**
 * ParentFeedbackSheet component
 * Accessible overlay form for sending in-app feedback from the parent dashboard.
 *
 * Implements US-008 (T4). Cloned from AddChildDialog.tsx (DD-009: ten accessible
 * overlays already ship, and AddChildDialog is the closest structural twin — a
 * multi-field form overlay with a required text field, an inline error box and
 * an isPending-only disable). Three responsive Tailwind deltas turn the cloned
 * centred dialog into a bottom sheet on mobile:
 *   - overlay: 'items-center' -> 'items-end md:items-center'
 *   - card:    'w-full max-w-sm mx-4' -> 'w-full md:max-w-sm md:mx-4'
 *   - card:    'rounded-2xl' -> 'rounded-t-2xl md:rounded-2xl'
 * AC-008.2's mobile/desktop behaviour additionally requires a manual browser
 * check at 375px and 1024px — jsdom cannot evaluate a media query (DD-7).
 *
 * DD-2 (submit button never disabled on invalid input): the submit button is
 * disabled ONLY while a request is in flight (mutation.isPending). Validation
 * runs inside handleSubmit and writes an inline client-owned message, exactly
 * as AddChildDialog.tsx:115-155 does. AC-008.4 requires the client to BOTH
 * block the send AND show what to fix; a disabled button can show nothing.
 *
 * DD-3 (fixed failure copy): errorCopyFor maps any server AuthError to ONE of
 * exactly two fixed client-owned strings, chosen by error class only. The
 * server's own message field and the axios interceptor's message are NEVER
 * rendered (NFR-2, AC-008.10). The 422 FEEDBACK_DISABLED kill-switch path is
 * treated identically to a 5xx, so it is indistinguishable to the parent
 * (IC-001 disclosure_rule).
 *
 * DD-5 / BR-008.2: hello@thinkotter.sg survives as a mailto anchor ONLY inside
 * this sheet's failure block — the one surviving mailto in the frontend after
 * this feature ships. AC-008.1's "no mail client invoked" is scoped to the tap
 * path (ParentDashboardPage's onClick), not to this failure recovery path.
 *
 * DD-8 / BR-008.1: no retry, no debounce, no ref guard, no idempotency key.
 * The disabled-while-pending submit button is the ENTIRE double-submit defence
 * (AC-008.8), and the double tap is proven by test (T6), never assumed.
 *
 * Deliberately NOT implemented, per DD-1 (aligned with DD-009): a Tab focus
 * trap. The one trap in this codebase (ReportIssueDialog) cycles between
 * exactly two button refs and does not generalise to a multi-field form, and
 * no other form overlay in this codebase has one.
 *
 * Reused components: Button/Label/Checkbox/Textarea (components/ui/), Loader2
 * from lucide-react (ReportIssueDialog.tsx:149-156 in-flight spinner pattern),
 * the single-select chip row pattern (PassageHighlightToolbar.tsx:40-74),
 * useSubmitFeedback (T2), FEEDBACK_CATEGORIES + buildFeedbackContext (T1).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSubmitFeedback } from '@/hooks/useSubmitFeedback';
import { buildFeedbackContext } from '@/lib/feedbackContext';
import { FEEDBACK_CATEGORIES } from '@/types/feedback';
import type { FeedbackCategory } from '@/types/feedback';
import type { AuthError } from '@/types/auth';

const MESSAGE_MAX_LENGTH = 2000;
const MESSAGE_MIN_LENGTH = 10;

// AC-008.7 / DD-3: exactly two fixed, client-owned failure strings. Never the
// server's message field, never the axios interceptor's message. The 422
// FEEDBACK_DISABLED kill-switch path must be indistinguishable from a 5xx.
const VALIDATION_FAILURE_COPY =
  'Please choose a category and write between 10 and 2000 characters.';
const GENERIC_FAILURE_COPY =
  'We could not send that just now. Please try again, or email us at hello@thinkotter.sg.';

/** Maps a server AuthError to one of exactly two fixed strings. Never returns error.message. */
function errorCopyForServerError(error: AuthError): string {
  if (error.code === 'VALIDATION_ERROR') {
    return VALIDATION_FAILURE_COPY;
  }
  return GENERIC_FAILURE_COPY;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ParentFeedbackSheetProps {
  /** Whether the sheet is visible */
  isOpen: boolean;
  /** Callback to close the sheet */
  onClose: () => void;
  /** The parent's account email, shown read only beside the reply-to-me checkbox. */
  accountEmail?: string;
  /** The parent's most recent session id across all children (DD-010), for auto-attached context. */
  mostRecentSessionId?: string;
  /** Called on a successful submission, before onClose — raises the toast the page owns. */
  onSuccess: () => void;
}

// ─── ParentFeedbackSheet (main export) ───────────────────────────────────────

/**
 * ParentFeedbackSheet — overlay form for sending in-app parent feedback.
 *
 * Fields:
 *  - Category (required, one of the five FEEDBACK_CATEGORIES chips)
 *  - Message (required, 10 to 2000 characters after trimming)
 *  - Reply to me (checkbox, default on, shows the account email read only)
 *
 * States:
 *  - Idle: form editable
 *  - Submitting (isPending): buttons disabled, spinner + "Sending..." label
 *  - Error (mutation.error or clientError): inline red alert, sheet stays open (TO-LL-036)
 *  - Success: a per-call mutate option (see handleSubmit) raises the toast via
 *    onSuccess(), then closes the sheet via onClose() — exactly once per
 *    submission (BUG-20260803-192629, RC-001).
 */
export function ParentFeedbackSheet({
  isOpen,
  onClose,
  accountEmail,
  mostRecentSessionId,
  onSuccess,
}: ParentFeedbackSheetProps) {
  const { submit, isPending, error, reset } = useSubmitFeedback();

  // ─── Form state ───────────────────────────────────────────────────────────
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [message, setMessage] = useState('');
  const [replyToMe, setReplyToMe] = useState(true);
  const [clientError, setClientError] = useState<string | null>(null);

  // Focus target: the first category chip (auto-focused on sheet open)
  const firstChipRef = useRef<HTMLButtonElement>(null);

  // ─── Reset state when the sheet opens ────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setCategory(null);
      setMessage('');
      setReplyToMe(true);
      setClientError(null);
      reset();
      const timer = setTimeout(() => {
        firstChipRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // BUG-20260803-192629 (RC-001, fixed 2026-08-04): there used to be a
  // `useEffect(() => { if (isSuccess) { onSuccess(); onClose(); } },
  // [isSuccess, onSuccess, onClose])` here. It is deliberately NOT coming
  // back. isSuccess has no release path in this component: the early return
  // below ("if (!isOpen) return null") only suppresses DOM output, it does
  // not unmount the component or the mutation instance, and the only
  // reset() call is gated on the isOpen-true branch above, so isSuccess
  // stays true for the rest of the page's life once a submission succeeds.
  // Deriving a side effect from that stale flag re-fired the toast/close
  // pair on every subsequent render whose onSuccess/onClose identity
  // differed (which, before RC-002, was every render). The fix moves the
  // side effect onto React Query's per-call mutate options in handleSubmit
  // below, which fire exactly once per settled mutation by construction.
  // Do NOT reintroduce an isSuccess-keyed effect, and do NOT "fix" the
  // staleness by adding a reset() on the close path above — that recreates
  // the confirmed AddChildDialog/RenameChildDialog reopen-snap-shut defect
  // (BUG-20260803-195602), which is the exact same reset-on-open /
  // close-on-success effect-ordering shape.

  // ─── Keyboard handler: Escape closes, but never while a request is in flight ─
  // This is what makes the "dismiss while in flight" edge case impossible: while
  // a send is pending the sheet cannot be dismissed, so there is nothing to
  // reopen and nothing to duplicate.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) {
        onClose();
      }
    },
    [onClose, isPending]
  );

  // ─── Form submission ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setClientError(null);

      // Client-side validation — blocks the API call on invalid input (AC-008.4).
      // The submit button itself is NEVER disabled for this reason (DD-2): a
      // disabled button could never show the parent what to fix.
      if (category === null) {
        setClientError('Please choose a category.');
        return;
      }

      const trimmed = message.trim();
      if (trimmed.length < MESSAGE_MIN_LENGTH) {
        setClientError('Please write at least 10 characters.');
        return;
      }
      if (trimmed.length > MESSAGE_MAX_LENGTH) {
        setClientError('Please keep your message to 2000 characters or fewer.');
        return;
      }

      // TO-LL-044: payload keys are exact camelCase matching IC-001.
      // No userId, no createdAt: IC-001's allowlist drops both (see T1).
      // BUG-20260803-192629 (RC-001): onSuccess then onClose are passed as a
      // per-call mutate option rather than derived from the isSuccess flag.
      // React Query invokes this exactly once when THIS submission settles,
      // so the toast is raised and the sheet closed exactly once per send,
      // never re-fired by an unrelated re-render. Order matters: onSuccess
      // (raise the toast flag) must run before onClose (clear the open
      // flag) — see ParentFeedbackSheet.test.tsx's success case.
      submit(
        {
          category,
          message: trimmed,
          replyToMe,
          context: buildFeedbackContext(mostRecentSessionId),
        },
        {
          onSuccess: () => {
            onSuccess();
            onClose();
          },
        }
      );
    },
    [category, message, replyToMe, mostRecentSessionId, submit, onSuccess, onClose]
  );

  // Don't render when not open
  if (!isOpen) return null;

  // Server error takes precedence — it is a real failure, not a stale client message.
  const serverErrorMessage = error ? errorCopyForServerError(error) : null;
  const errorMessage = serverErrorMessage ?? clientError;

  return (
    /* Overlay — cloned from AddChildDialog (role=dialog, aria-modal, fixed inset-0 z-50).
       Delta 1: items-end on mobile (bottom sheet), md:items-center (centred dialog). */
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 md:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-sheet-title"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        // Close on backdrop click (only if not submitting) — never while pending.
        if (e.target === e.currentTarget && !isPending) {
          onClose();
        }
      }}
    >
      {/* Sheet card. Delta 2: w-full md:max-w-sm md:mx-4. Delta 3: rounded-t-2xl md:rounded-2xl.
          max-h-[90vh] overflow-y-auto so a long message plus the on-screen keyboard cannot push
          the submit button off a phone screen. */}
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white p-6 shadow-lg md:mx-4 md:max-w-sm md:rounded-2xl md:p-8">
        {/* Title */}
        <h2
          id="feedback-sheet-title"
          className="text-lg font-bold text-gray-900 mb-1 md:text-xl"
        >
          Share feedback
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Tell us what is going well and what is not.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Category chips (AC-008.3) */}
          <div className="space-y-1">
            <Label className="text-sm font-medium text-gray-700">
              What is this about? <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <div role="group" aria-label="Feedback category" className="flex flex-wrap gap-2">
              {FEEDBACK_CATEGORIES.map(({ value, label }, index) => {
                const isSelected = category === value;
                return (
                  <button
                    key={value}
                    ref={index === 0 ? firstChipRef : undefined}
                    type="button"
                    aria-pressed={isSelected}
                    disabled={isPending}
                    onClick={() => setCategory(value)}
                    className={cn(
                      'min-h-11 rounded-full border px-3.5 text-sm font-semibold transition-colors',
                      isSelected
                        ? 'border-otter-orange-500 bg-otter-orange-500 text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message field (AC-008.3) */}
          <div className="space-y-1">
            <Label htmlFor="feedback-message" className="text-sm font-medium text-gray-700">
              Your message <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what happened."
              rows={5}
              maxLength={MESSAGE_MAX_LENGTH}
              disabled={isPending}
            />
            <p className="text-xs text-gray-400">
              {message.length} of {MESSAGE_MAX_LENGTH}
            </p>
          </div>

          {/* Reply-to-me row (AC-008.3) — the account email is display only, never editable. */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Checkbox
                id="feedback-reply-to-me"
                checked={replyToMe}
                onCheckedChange={setReplyToMe}
                disabled={isPending}
              />
              <Label htmlFor="feedback-reply-to-me" className="text-sm font-medium text-gray-700">
                Reply to me at this address
              </Label>
            </div>
            {accountEmail && (
              <p className="pl-7 text-xs text-gray-500">{accountEmail}</p>
            )}
          </div>

          {/* Inline error message — surfaced, never swallowed (TO-LL-036) */}
          {errorMessage && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
            >
              <p>{errorMessage}</p>
              {/* BR-008.2 / DD-5: the ONE surviving mailto in the frontend, shown ONLY
                  inside this failure block, and only for an actual submission failure
                  (never for a client-side validation message). */}
              {serverErrorMessage && (
                <a
                  href="mailto:hello@thinkotter.sg?subject=ThinkOtter Feedback"
                  className="mt-1 inline-block font-semibold underline"
                >
                  hello@thinkotter.sg
                </a>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={isPending} className="flex-1 h-11">
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending…
                </>
              ) : (
                'Send feedback'
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={onClose}
              className="flex-1 h-11"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
